import { useCallback, useState } from "react";
import { api, checkImageFile, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, Camera } from "lucide-react";

import { tr } from "../lib/i18n";
/** Shrink large JPG/PNG/WEBP photos in the browser (long side 1920 px) before upload. HEIC is sent as-is. */
async function compressImage(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1920 / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export default function PhotoUpload({ onUploaded, testId = "photo-upload" }) {
  const [busy, setBusy] = useState(false);

  const upload = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;
      setBusy(true);
      try {
        for (const file of files) {
          try {
            const bad = await checkImageFile(file);
            if (bad) { toast.error(bad); continue; }
            const form = new FormData();
            form.append("file", await compressImage(file));
            const { data } = await api.post("/uploads/photo", form, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 120000,
            });
            onUploaded?.(data);
          } catch (e) {
            toast.error(formatApiError(e));
          }
        }
      } finally {
        setBusy(false);
      }
    },
    [onUploaded],
  );

  return (
    <div className="flex flex-wrap gap-3">
      <label
        data-testid={`${testId}-picker`}
        className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-[hsl(var(--border))] text-sm hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
      >
        <UploadCloud size={16} />
        {busy ? tr("x.uploading") : tr("x.upload_photos")}
        <input
          type="file"
          accept="image/*"
          multiple
          hidden
          disabled={busy}
          data-testid={`${testId}-input`}
          onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }}
        />
      </label>
      <label
        data-testid={`${testId}-camera`}
        className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-[hsl(var(--border))] text-sm hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors md:hidden"
      >
        <Camera size={16} />
        {tr("x.camera")}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          disabled={busy}
          onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }}
        />
      </label>
    </div>
  );
}
